/* eslint-disable @typescript-eslint/no-explicit-any */
import Vue from 'vue';
import { Context } from '@nuxt/types';

/**
 * Self-contained enrollPatient "test mode" — everything lives in this one file so it
 * can be dropped into another repo with minimal wiring.
 *
 * USAGE
 *   Append ?mockEnroll=<key> to any URL (e.g. /?mockEnroll=ageRestriction). The key
 *   is one of FIXTURES below. It persists in sessionStorage for the session; clear
 *   with ?mockEnroll=off. A red TESTING MODE banner shows while active. The enroll
 *   call returns the canned fixture instead of hitting the backend, so no real
 *   enrollment is created. Add a scenario by adding an entry to FIXTURES.
 *
 * WIRING (per repo — two steps)
 *   1. Register this file in nuxt.config `plugins` (after the DI/axios plugin).
 *   2. At the very top of your enroll API call, short-circuit when active:
 *
 *        import { getEnrollFixture, mockEnrollState } from '~/plugins/mock-enroll.client';
 *        ...
 *        if (mockEnrollState.activeKey) {
 *            const fixture = getEnrollFixture(mockEnrollState.activeKey);
 *            if (fixture) {
 *                return { status: fixture.statusCode, data: fixture.response } as AxiosResponse;
 *            }
 *        }
 */

export interface EnrollFixture {
    statusCode: number;
    /** The exact pass-through body the UI receives: { data: {...}, messages: [], success } */
    response: any;
}

// ---------------------------------------------------------------------------
// Fixtures. Add a scenario by adding an entry here; the key is the ?mockEnroll value.
// ---------------------------------------------------------------------------
export const FIXTURES: { [key: string]: EnrollFixture } = {
    // AgeRestriction rule fails -> rejection
    ageRestriction: {
        statusCode: 200,
        response: {
            data: {
                allEligibilityResults: {
                    GovtExclEbvCheck: [{ rejectReasonCode: null, passed: true, rejectReasonDescription: null }],
                    MinimumAgeRule: [{ rejectReasonCode: 'AgeRestriction', passed: false, rejectReasonDescription: 'Age restriction' }],
                },
                evaluationResult: { eligibility: 2, scenario: 1, existingEnrollmentId: null },
                enrollment: {
                    accountId: 4110355,
                    caseId: 25162133,
                    memberNumbers: [],
                    caseSubStatusCode: 'Rejected',
                    caseStatusCode: 'Complete',
                    status: { status: 1, conditions: [{ caseId: 25162133, code: 'AgeRestriction', description: 'Age restriction', categoryCode: 'Patient' }] },
                    enrollmentFlags: { isActiveEnrollmentForCurrentDate: false },
                },
            },
            messages: [],
            success: true,
        },
    },

    // InsTypeNAPCF rule fails, no prior enrollment -> rejection
    'insTypeNapcf-rejected': {
        statusCode: 200,
        response: {
            data: {
                allEligibilityResults: {
                    GovtExclEbvCheck: [{ rejectReasonCode: null, passed: true, rejectReasonDescription: null }],
                    MinimumAgeRule: [{ rejectReasonCode: null, passed: true, rejectReasonDescription: null }],
                    PlanTypeEligibility: [{ rejectReasonCode: 'InsTypeNAPCF', passed: false, rejectReasonDescription: 'Insurance Type Not Allowed - Pharmacy Plan Type' }],
                },
                evaluationResult: { eligibility: 2, scenario: 1, existingEnrollmentId: null },
                enrollment: {
                    accountId: 4267207,
                    caseId: 25160665,
                    memberNumbers: [],
                    caseSubStatusCode: 'Rejected',
                    caseStatusCode: 'Complete',
                    status: { status: 1, conditions: [{ caseId: 25160665, code: 'InsTypeNAPCF', description: 'Insurance Type Not Allowed - Pharmacy Plan Type', categoryCode: '' }] },
                    enrollmentFlags: { isActiveEnrollmentForCurrentDate: false },
                },
            },
            messages: [],
            success: true,
        },
    },

    // InsTypeNAPCF rule fails BUT an existing active enrollment returns a member number.
    // Under reject-code-aware routing the failed rule wins -> rejection.
    'insTypeNapcf-existing-enrollment': {
        statusCode: 200,
        response: {
            data: {
                allEligibilityResults: {
                    GovtExclEbvCheck: [{ rejectReasonCode: null, passed: true, rejectReasonDescription: null }],
                    MinimumAgeRule: [{ rejectReasonCode: null, passed: true, rejectReasonDescription: null }],
                    PlanTypeEligibility: [{ rejectReasonCode: 'InsTypeNAPCF', passed: false, rejectReasonDescription: 'Insurance Type Not Allowed - Pharmacy Plan Type' }],
                },
                evaluationResult: { eligibility: 2, scenario: 2, existingEnrollmentId: 25108764 },
                enrollment: {
                    accountId: 4251666,
                    caseId: 25108764,
                    startDate: '2026-06-19T00:00:00Z',
                    endDate: '2026-12-31T00:00:00Z',
                    memberNumbers: [{ number: '48378249511', type: 7, paymentMethod: 10002 }],
                    caseSubStatusCode: 'Enrolled',
                    caseStatusCode: 'Complete',
                    status: { status: 6, conditions: [] },
                    enrollmentFlags: { isActiveEnrollmentForCurrentDate: true },
                },
            },
            messages: [],
            success: true,
        },
    },

    // All rules pass with a member number -> success
    success: {
        statusCode: 200,
        response: {
            data: {
                allEligibilityResults: {
                    GovtExclEbvCheck: [{ rejectReasonCode: null, passed: true, rejectReasonDescription: null }],
                    MinimumAgeRule: [{ rejectReasonCode: null, passed: true, rejectReasonDescription: null }],
                    PlanTypeEligibility: [{ rejectReasonCode: null, passed: true, rejectReasonDescription: null }],
                },
                evaluationResult: { eligibility: 1, scenario: 1, existingEnrollmentId: null },
                enrollment: {
                    accountId: 4270000,
                    caseId: 25170000,
                    startDate: '2026-06-26T00:00:00Z',
                    endDate: '2026-12-31T00:00:00Z',
                    memberNumbers: [{ number: '99999999999', type: 7, paymentMethod: 10002 }],
                    caseSubStatusCode: 'Enrolled',
                    caseStatusCode: 'Complete',
                    status: { status: 6, conditions: [] },
                    enrollmentFlags: { isActiveEnrollmentForCurrentDate: true },
                },
            },
            messages: [],
            success: true,
        },
    },

    // Non-200 -> error
    error: {
        statusCode: 400,
        response: { data: null, messages: ['Enrollment request failed.'], success: false },
    },
};

export function getEnrollFixture(key: string): EnrollFixture | null {
    return FIXTURES[key] || null;
}

// ---------------------------------------------------------------------------
// Reactive active-key state (shared singleton; read by the enroll short-circuit).
// ---------------------------------------------------------------------------
export const mockEnrollState = Vue.observable({ activeKey: '' });

const STORAGE_KEY = 'mockEnroll';

function setKey(key: string) {
    mockEnrollState.activeKey = key;
    if (key) {
        window.sessionStorage.setItem(STORAGE_KEY, key);
    } else {
        window.sessionStorage.removeItem(STORAGE_KEY);
    }
}

function applyFromQuery(raw: unknown) {
    if (typeof raw !== 'string' || raw.length === 0) {
        return;
    }
    setKey(raw.toLowerCase() === 'off' ? '' : raw);
}

// ---------------------------------------------------------------------------
// Banner — self-mounted to <body> so no layout/component files are needed.
// ---------------------------------------------------------------------------
function mountBanner(connectTo: string) {
    if (typeof document === 'undefined') {
        return;
    }
    const host = document.createElement('div');
    document.body.append(host);
    new Vue({
        render(h) {
            const active = !!mockEnrollState.activeKey;
            return h(
                'div',
                {
                    style: {
                        display: active ? 'block' : 'none',
                        position: 'fixed',
                        top: '0',
                        left: '0',
                        right: '0',
                        zIndex: '11000',
                        background: '#b71c1c',
                        color: '#fff',
                        textAlign: 'center',
                        fontWeight: '700',
                        fontSize: '14px',
                        padding: '6px 12px',
                        letterSpacing: '0.02em',
                    },
                },
                [
                    `⚠ TESTING MODE — enrollPatient response is mocked: ${mockEnrollState.activeKey}`,
                    connectTo ? ` · connected to: ${connectTo}` : '',
                ]
            );
        },
    }).$mount(host);
}

// ---------------------------------------------------------------------------
// Nuxt plugin entry.
// ---------------------------------------------------------------------------
export default (context: Context) => {
    // Hydrate a prior selection, then apply the current URL (handles ?mockEnroll=off).
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
        mockEnrollState.activeKey = stored;
    }
    applyFromQuery(context.route.query.mockEnroll);
    if (context.app.router) {
        context.app.router.afterEach(to => applyFromQuery(to.query.mockEnroll));
    }

    const env: any = context.env || {};
    const connectTo = env.ConnectTo || env.connectTo || env.CONNECTTO || '';
    mountBanner(String(connectTo));
};
